"use client";

import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import type { MatchStep, MatchStepId } from "@/entities/bracket/lib/match-lifecycle";

const ORDERED_STEP_IDS: MatchStepId[] = [
  "tauri_running",
  "predraft_done",
  "opponent_ready",
  "lobby_created",
  "all_in_lobby",
  "in_game",
  "result_submitted",
];

interface MatchChecklistProps {
  steps: MatchStep[];
  className?: string;
  stepActions?: Partial<Record<MatchStepId, ReactNode>>;
}

export function MatchChecklist({
  steps,
  className,
  stepActions,
}: MatchChecklistProps) {
  const t = useTranslations("Match.checklist");

  const sortedSteps = [...steps].sort(
    (a, b) =>
      ORDERED_STEP_IDS.indexOf(a.id) - ORDERED_STEP_IDS.indexOf(b.id),
  );

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{t("title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-3">
          {sortedSteps.map((step) => {
            const Icon =
              step.status === "done"
                ? CheckCircle2
                : step.status === "active"
                  ? Loader2
                  : Circle;

            const iconClass =
              step.status === "done"
                ? "text-primary"
                : step.status === "active"
                  ? "text-primary animate-spin"
                  : "text-muted-foreground";

            const titleClass =
              step.status === "done"
                ? "line-through text-muted-foreground"
                : step.status === "active"
                  ? "text-foreground font-semibold"
                  : "text-muted-foreground";

            const hintClass =
              step.status === "active"
                ? "text-muted-foreground"
                : "text-muted-foreground/70";

            const action =
              step.status === "active" ? stepActions?.[step.id] : null;

            return (
              <li key={step.id} className="flex gap-3">
                <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconClass}`} />
                <div className="flex-1 space-y-2">
                  <div>
                    <p className={`${titleClass} text-base`}>
                      {t(`${step.id}.title`)}
                    </p>
                    {step.status !== "done" && (
                      <p className={`${hintClass} text-sm`}>
                        {t(`${step.id}.hint`)}
                      </p>
                    )}
                  </div>
                  {action}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
